// Cadastro de cartão de crédito pela tela (Lucas, 22/09/2026: "Eu consigo
// cadastrar um novo cartão de crédito?" — não conseguia: cartão só entrava pelo
// "Sincronizar do CA", e o Conta Azul foi desligado em 15/07).
//
// A régua de verdade mora no servidor (dre-engine/handlers/cartaoCadastro.js):
// banco da lista fechada, fechamento e vencimento obrigatórios, conta que paga
// = conta corrente ativa, e nome/apelido que não repitam nenhuma conta, cartão
// ou lançamento — o nome é por onde a fatura acha as compras (e vira também o
// apelido da conta: a Nova despesa grava o apelido na despesa, e apelido ≠ nome
// deixava compra fora da fatura). Aqui a tela só pergunta o que o servidor
// precisa e explica o efeito de cada campo.
import { useEffect, useMemo, useState } from 'react';
import { App as AntApp, Modal, Form, Input, InputNumber, Select, Alert, Flex, Typography } from 'antd';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import { BANCOS, rotuloBanco } from '../financeiro-contas/ModalContaFinanceira';

const { Text } = Typography;

// Bandeiras mais comuns (campo opcional). No banco, 'caixa' é a Caixa Econômica.
const BANDEIRAS = ['MASTERCARD', 'VISA', 'ELO', 'AMEX', 'HIPERCARD'];

export type CartaoCriado = {
  id: string; nome: string; banco: string | null; bandeira: string | null;
  final_cartao: string | null; conta_pagamento_nome: string | null;
  dia_fechamento: number | null; dia_vencimento: number | null; ativo: boolean;
};

type ContaCorrente = { id: string; nome: string; apelido: string | null; banco: string; tipo: string; ativo: boolean };

export function ModalNovoCartao({ onFechar, onSalvou }: {
  onFechar: () => void;
  onSalvou: (cartao: CartaoCriado) => void;
}) {
  const { message } = AntApp.useApp();
  const [banco, setBanco] = useState<string | undefined>();
  const [nome, setNome] = useState('');
  const [nomeMexido, setNomeMexido] = useState(false);
  const [bandeira, setBandeira] = useState<string | undefined>();
  const [finalCartao, setFinalCartao] = useState('');
  const [fech, setFech] = useState<number | null>(null);
  const [venc, setVenc] = useState<number | null>(null);
  const [contaPaga, setContaPaga] = useState<string | undefined>();
  const [contaMexida, setContaMexida] = useState(false);
  const [correntes, setCorrentes] = useState<ContaCorrente[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  // Contas que podem pagar a fatura: correntes ativas do cadastro.
  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const token = await getToken();
        const r = await fetch(`${API_URL}/financeiro/contas`, { headers: { Authorization: token } });
        const d0 = await r.json().catch(() => []);
        const lista: ContaCorrente[] = Array.isArray(d0) ? d0 : (d0?.body ? JSON.parse(d0.body) : []);
        if (vivo) setCorrentes(lista.filter(c => c.tipo === 'corrente' && c.ativo !== false));
      } catch { /* sem a lista, o servidor ainda confere — o erro aparece ao salvar */ }
    })();
    return () => { vivo = false; };
  }, []);

  // Escolheu (ou trocou) o banco: sugere nome e a corrente do mesmo banco para
  // pagar — enquanto a pessoa não escolheu outra coisa à mão.
  useEffect(() => {
    if (!banco) return;
    if (!nomeMexido) setNome(`Cartão ${rotuloBanco(banco)} - Hydrodata`);
    if (!contaMexida) {
      const doBanco = correntes.find(c => c.banco === banco);
      setContaPaga(doBanco ? doBanco.nome : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banco, correntes]);

  // Exemplo concreto do ciclo, para a pessoa conferir com a fatura do banco.
  const exemplo = useMemo(() => {
    if (!fech || !venc || fech === venc) return null;
    const mesVenc = venc <= fech ? 'do mês seguinte' : 'do mesmo mês';
    return `Compra até o dia ${fech} entra na fatura que vence dia ${venc} ${mesVenc}; depois do dia ${fech}, na fatura seguinte.`;
  }, [fech, venc]);

  async function salvar() {
    setErro(null);
    if (!banco) { setErro('Escolha o banco do cartão.'); return; }
    if (!nome.trim()) { setErro('Dê um nome ao cartão.'); return; }
    if (!fech || !venc) { setErro('Informe o dia de fechamento e o dia de vencimento da fatura.'); return; }
    if (!contaPaga) { setErro('Escolha a conta que paga a fatura.'); return; }
    setSalvando(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_URL}/financeiro/cartoes`, {
        method: 'POST',
        headers: { Authorization: token, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: nome.trim(), banco, bandeira: bandeira || '',
          final_cartao: finalCartao.trim(), dia_fechamento: fech, dia_vencimento: venc,
          conta_pagamento_nome: contaPaga,
        }),
      });
      const d0 = await r.json().catch(() => ({}));
      const d = d0 && d0.body ? JSON.parse(d0.body) : d0;
      if (!r.ok || d?.erro || d?.error) {
        setErro(d?.erro || d?.error || 'Não consegui cadastrar. Tente de novo.');
        return;
      }
      message.success(`Cartão "${d.cartao?.nome}" cadastrado.`);
      onSalvou(d.cartao);
    } catch (e: any) {
      setErro('Não consegui falar com o servidor: ' + (e?.message || e));
    } finally { setSalvando(false); }
  }

  return (
    <Modal
      open
      title="Novo cartão de crédito"
      onCancel={onFechar}
      onOk={salvar}
      okText="Cadastrar cartão"
      cancelText="Cancelar"
      confirmLoading={salvando}
      width="94vw"
      style={{ maxWidth: 680, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 170px)', overflowY: 'auto' } }}
      destroyOnHidden
    >
      <Form layout="vertical" size="middle" style={{ marginTop: 8 }}>
        <Form.Item label="Banco" required style={{ marginBottom: 12 }}>
          <Select value={banco} onChange={setBanco} placeholder="Escolha o banco"
            options={BANCOS.map(b => ({ value: b, label: rotuloBanco(b) }))} />
        </Form.Item>

        <Form.Item label="Nome do cartão" required style={{ marginBottom: 12 }}
          extra="É o nome que aparece na Nova despesa e por onde o sistema acha as compras do cartão. Não pode repetir o de outra conta e não muda depois.">
          <Input value={nome} maxLength={60}
            onChange={(e) => { setNome(e.target.value); setNomeMexido(true); }}
            placeholder='Ex.: "Cartão Safra - Hydrodata"' />
        </Form.Item>

        <Flex gap={12} wrap="wrap">
          <Form.Item label="Dia de fechamento" required style={{ marginBottom: 12, flex: '1 1 140px' }}>
            <InputNumber style={{ width: '100%' }} min={1} max={31} precision={0}
              value={fech} onChange={(v) => setFech(v == null ? null : Number(v))} placeholder="Ex.: 3" />
          </Form.Item>
          <Form.Item label="Dia de vencimento" required style={{ marginBottom: 12, flex: '1 1 140px' }}>
            <InputNumber style={{ width: '100%' }} min={1} max={31} precision={0}
              value={venc} onChange={(v) => setVenc(v == null ? null : Number(v))} placeholder="Ex.: 10" />
          </Form.Item>
        </Flex>
        {exemplo && <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: -4, marginBottom: 12 }}>{exemplo}</Text>}

        <Form.Item label="Conta que paga a fatura" required style={{ marginBottom: 12 }}
          extra="A saída do pagamento aparece no extrato desta conta — é lá que a Marina concilia.">
          <Select value={contaPaga} onChange={(v) => { setContaPaga(v); setContaMexida(true); }} placeholder="Escolha a conta corrente"
            options={correntes.map(c => ({ value: c.nome, label: c.apelido || c.nome }))} />
        </Form.Item>

        <Flex gap={12} wrap="wrap">
          <Form.Item label="Bandeira" style={{ marginBottom: 12, flex: '1 1 180px' }}>
            <Select value={bandeira} onChange={setBandeira} allowClear placeholder="(opcional)"
              options={BANDEIRAS.map(b => ({ value: b, label: b }))} />
          </Form.Item>
          <Form.Item label="Final do cartão" style={{ marginBottom: 12, flex: '0 1 150px' }}
            extra="Só os 4 últimos números.">
            {/* Sem maxLength: colar o número inteiro guardaria os 4 PRIMEIROS.
                Fica só com os 4 últimos — o número inteiro nunca sai daqui. */}
            <Input value={finalCartao} inputMode="numeric"
              onChange={(e) => setFinalCartao(e.target.value.replace(/\D/g, '').slice(-4))} placeholder="3147" />
          </Form.Item>
        </Flex>

        <Alert type="info" showIcon style={{ marginBottom: 8 }} message={
          <span style={{ fontSize: 12.5 }}>
            Depois de cadastrado, o cartão já aparece como conta de pagamento na <b>Nova despesa</b> (com o
            vencimento sugerido pela fatura), aceita <b>Nova compra</b>, <b>Importar CSV</b> e <b>Pagar fatura</b> aqui.
          </span>} />
        {erro && <Alert type="error" showIcon message={erro} />}
      </Form>
    </Modal>
  );
}
